import React, { useState, useMemo } from 'react';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import {
    Box,
    Stack,
    Typography,
    Tooltip,
    useTheme,
    Collapse,
    IconButton,
    Paper,
    Grid,
    useMediaQuery,
} from '@mui/material';
import {
    Lock as LockIcon,
    Public as PublicIcon,
    Notes as NotesIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { IconWithText } from '../styles/components';
import { isMobile, isTablet } from 'react-device-detect';

interface OrderNotesProps {
    order: OrderType;
    active_task_notes?: string[];
    showTitle?: boolean;
    expandedByDefault?: boolean;
}

const getTaskNotes = (order: OrderType) => [
    ...(order.last_delivery_task?.notes ?? []),
    ...(order.last_pickup_task?.notes ?? []),
];

const hasContent = (val?: string | string[]) =>
    Array.isArray(val) ? val.length > 0 : !!val?.trim();

const isLongContent = (val?: string | string[]) =>
    Array.isArray(val) ? val.length > 3 : val && val.length > 120;

const NoteItem = ({ text, isRecent = false }: { text: string; isRecent?: boolean }) => (
    <Tooltip title={text} placement="top-start">
        <Typography variant="body2" sx={{
            fontSize: '0.75rem',
            color: isRecent ? 'error.main' : 'text.secondary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.5,
            '&:hover': { color: isRecent ? 'error.dark' : 'text.primary' }
        }}>
            {text}
        </Typography>
    </Tooltip>
);

const OrderNotes = ({ order, active_task_notes = [], showTitle = false, expandedByDefault = false }: OrderNotesProps) => {
    const [expanded, setExpanded] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { public_note, private_note, task_notes = [] } = order;
    const recentNotes = getTaskNotes(order);
    const hasRecentNotes = hasContent(recentNotes) || hasContent(active_task_notes);

    // Define all possible sections
    const allSections = [
        {
            content: public_note,
            icon: <PublicIcon fontSize="small" color="info" />,
            label: 'Public',
            isPrimary: true,
            mobileGridCells: 6,
        },
        {
            content: private_note,
            icon: <LockIcon fontSize="small" color="warning" />,
            label: 'Private',
            isPrimary: true,
            mobileGridCells: 6,
        },
        {
            content: hasRecentNotes ? [...(recentNotes || []), ...(active_task_notes || [])] : [],
            icon: <NotesIcon fontSize="small" color="error" />,
            isRecent: true,
            mobileGridCells: 12,
        },
        {
            content: task_notes,
            icon: <NotesIcon fontSize="small" color="disabled" />,
            mobileGridCells: 12,
        }
    ].filter(s => hasContent(s.content));

    if (allSections.length === 0) return null;

    // Separate primary (Public/Private) and secondary sections
    const primarySections = allSections.filter(s => s.isPrimary);
    const secondarySections = allSections.filter(s => !s.isPrimary);

    // Determine what to show in collapsed state
    const visibleWhenCollapsed = primarySections.length > 0
        ? primarySections.slice(0, 2) // Max 2 (Public/Private)
        : secondarySections.slice(0, 1); // Fallback to Recent

    const hiddenWhenCollapsed = [
        ...primarySections.slice(2),
        ...secondarySections.slice(primarySections.length > 0 ? 0 : 1)
    ].filter(s => s);

    const shouldCollapse = hiddenWhenCollapsed.length > 0 ||
        visibleWhenCollapsed.some(s => isLongContent(s.content));

    return (
        <Paper elevation={0} sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
        }}>
            <Stack spacing={1}>
                {showTitle && (
                    <Box sx={{ textAlign: 'center', mb: 0.5 }}>
                        <IconWithText centered>
                            <NotesIcon color="primary" fontSize="small" />
                            <>Notes</>
                        </IconWithText>
                    </Box>
                )}

                {/* Always visible content - side by side on mobile */}
                {(isMobile && !isTablet) ? (
                    <Grid container spacing={1}>
                        {visibleWhenCollapsed.map((section, i) => (
                            <Grid item xs={section.mobileGridCells} key={i}>
                                {typeof section.content === 'string' ? (
                                    <Stack spacing={0.5}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {section.icon}
                                            <Typography variant="caption" color="text.secondary">
                                                {section.label}
                                            </Typography>
                                        </Stack>
                                        <Box sx={{ pl: 3.5 }}>
                                            <Typography variant="body2" sx={{
                                                fontSize: '0.75rem',
                                                color: section.isRecent ? 'error.main' : 'text.secondary',
                                                whiteSpace: 'pre-line',
                                                display: '-webkit-box',
                                                WebkitLineClamp: shouldCollapse && !expanded ? 3 : 'unset',
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                lineHeight: 1.5
                                            }}>
                                                {section.content}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                ) : (
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {section.icon}
                                            {section.label && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {section.label}
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Box sx={{ pl: 3.5 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                                {(shouldCollapse && !expanded ? section.content.slice(0, 3) : section.content).map((text, j) => (
                                                    <React.Fragment key={j}>
                                                        <NoteItem text={text} isRecent={section.isRecent} />
                                                        {j < section.content.length - 1 && (
                                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.disabled', mx: 0.5 }}>
                                                                |
                                                            </Typography>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            </Grid>
                        ))}
                        {/* Fill empty grid cells if needed */}
                        {visibleWhenCollapsed.length === 1 && <Grid item xs={6} />}
                    </Grid>
                ) : (
                    /* Desktop view - single column */
                    visibleWhenCollapsed.map((section, i) => (
                        <Box key={i}>
                            {typeof section.content === 'string' ? (
                                <Stack spacing={0.5}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {section.icon}
                                        <Typography variant="caption" color="text.secondary">
                                            {section.label}
                                        </Typography>
                                    </Stack>
                                    <Box sx={{ pl: 3.5 }}>
                                        <Typography variant="body2" sx={{
                                            fontSize: '0.75rem',
                                            color: section.isRecent ? 'error.main' : 'text.secondary',
                                            whiteSpace: 'pre-line',
                                            display: '-webkit-box',
                                            WebkitLineClamp: shouldCollapse && !expanded ? 3 : 'unset',
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            lineHeight: 1.5
                                        }}>
                                            {section.content}
                                        </Typography>
                                    </Box>
                                </Stack>
                            ) : (
                                <Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {section.icon}
                                        {section.label && (
                                            <Typography variant="caption" color="text.secondary">
                                                {section.label}
                                            </Typography>
                                        )}
                                    </Stack>
                                    <Box sx={{ pl: 3.5 }}>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                            {(shouldCollapse && !expanded ? section.content.slice(0, 3) : section.content).map((text, j) => (
                                                <React.Fragment key={j}>
                                                    <NoteItem text={text} isRecent={section.isRecent} />
                                                    {j < section.content.length - 1 && (
                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.disabled', mx: 0.5 }}>
                                                            |
                                                        </Typography>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ))
                )}

                {/* Collapsible sections */}
                {shouldCollapse && (
                    <>
                        <Collapse in={expanded}>
                            <Stack spacing={1}>
                                {hiddenWhenCollapsed.map((section, i) => (
                                    <Box key={i}>
                                        {typeof section.content === 'string' ? (
                                            <Stack spacing={0.5}>
                                                {section.label && (
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        {section.icon}
                                                        <Typography variant="caption" color="text.secondary">
                                                            {section.label}
                                                        </Typography>
                                                    </Stack>
                                                )}
                                                <Box sx={{ pl: section.label ? 3.5 : 0 }}>
                                                    <Typography variant="body2" sx={{
                                                        fontSize: '0.75rem',
                                                        color: section.isRecent ? 'error.main' : 'text.secondary',
                                                        whiteSpace: 'pre-line',
                                                        lineHeight: 1.5
                                                    }}>
                                                        {section.content}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        ) : (
                                            <Box sx={{ pl: section.label ? 3.5 : 0 }}>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                                    {section.content.map((text, j) => (
                                                        <React.Fragment key={j}>
                                                            <NoteItem text={text} isRecent={section.isRecent} />
                                                            {j < section.content.length - 1 && (
                                                                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.disabled', mx: 0.5 }}>
                                                                    |
                                                                </Typography>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        </Collapse>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                            <IconButton
                                size="small"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(!expanded) }}
                                sx={{ p: 0 }}
                            >
                                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                    {expanded ? 'Less' : 'More'}
                                </Typography>
                            </IconButton>
                        </Box>
                    </>
                )}
            </Stack>
        </Paper>
    );
};

export default OrderNotes;